import {
    useCallback,
    useRef,
    useState,
} from 'react';

export type Task = () => Promise<boolean>;

const useTaskQueue = (): {
    enqueue: (task: Task) => void;
    dequeue: () => Task | null;
    runNext: () => Promise<boolean>;
    runNextWithoutDequeue: () => Promise<boolean>;
    flush: () => Promise<void>;
    clear: () => void;
    size: number; version: number;
} => {
    const queueRef = useRef<Task[]>([]);
    const runningRef = useRef(false);
    const [version, setVersion] = useState(0); // for optional UI updates

    const size = queueRef.current.length;

    const enqueue = useCallback((task: Task) => {
        queueRef.current.push(task);
        setVersion((v) => v + 1);
    }, []);

    const dequeue = useCallback(() => {
        const t = queueRef.current.shift() ?? null;
        setVersion((v) => v + 1);
        return t;
    }, []);

    const clear = useCallback(() => {
        queueRef.current = [];
        setVersion((v) => v + 1);
    }, []);

    const runNext = useCallback(async () => {
        const task = queueRef.current.shift();
        if (!task) return false;
        try {
            return await task();
        } finally {
            setVersion((v) => v + 1);
        }
    }, []);

    const runNextWithoutDequeue = useCallback(async () => {
        const task = queueRef.current[0];
        if (!task) return false;
        try {
            return await task();
        } finally {
            setVersion((v) => v + 1);
        }
    }, []);

    const flush = useCallback(async () => {
        if (runningRef.current) return; // prevent overlapping flushes
        runningRef.current = true;
        try {
            while (queueRef.current.length) {
                const t = queueRef.current[0];
                // eslint-disable-next-line no-await-in-loop
                await t();
                queueRef.current.shift();
            }
        } finally {
            runningRef.current = false;
            setVersion((v) => v + 1);
        }
    }, []);

    return {
        enqueue,
        dequeue,
        runNext,
        runNextWithoutDequeue,
        flush,
        clear,
        size,
        version,
    };
};

export default useTaskQueue;
